import { 
  AppConfig, 
  Build, 
  DashboardStats, 
  Template, 
  Asset,
  User,
  BuildLog 
} from '../types'

// Mock User
export const mockUser: User = {
  id: 'user_123',
  email: 'john@example.com',
  name: 'John Smith',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
  plan: 'pro',
  limits: {
    maxApps: 10,
    maxBuildsPerMonth: 50,
    maxStorageSize: 5368709120 // 5GB in bytes
  },
  usage: {
    appsCount: 3,
    buildsThisMonth: 12,
    storageUsed: 1073741824 // 1GB in bytes
  },
  createdAt: '2024-01-15T10:30:00Z'
}

// Mock Assets
export const mockAssets: Asset[] = [
  {
    id: 'asset_1',
    name: 'restaurant-icon.png',
    type: 'icon',
    url: '/uploads/icons/restaurant-icon.png',
    size: 45632,
    dimensions: { width: 1024, height: 1024 },
    uploadedAt: '2024-12-08T10:30:00Z'
  },
  {
    id: 'asset_2', 
    name: 'splash-screen.png',
    type: 'splash',
    url: '/uploads/splash/splash-screen.png',
    size: 128450,
    dimensions: { width: 1125, height: 2436 },
    uploadedAt: '2024-12-08T10:35:00Z'
  }
]

// Mock Build Logs
const mockBuildLogs: BuildLog[] = [
  {
    id: 'log_1',
    timestamp: '2024-12-08T14:25:00Z',
    level: 'info',
    message: 'Starting app generation process',
    details: 'Initializing template processing for iOS Swift WebView template'
  },
  {
    id: 'log_2',
    timestamp: '2024-12-08T14:25:15Z',
    level: 'info',
    message: 'Processing template placeholders',
    details: 'Replacing {{APP_NAME}} with "My Restaurant App"'
  },
  {
    id: 'log_3',
    timestamp: '2024-12-08T14:25:30Z',
    level: 'info',
    message: 'Generating app icons',
    details: 'Creating icons for all required sizes: 20x20 through 1024x1024'
  },
  {
    id: 'log_4',
    timestamp: '2024-12-08T14:26:00Z',
    level: 'info',
    message: 'Configuring Xcode project',
    details: 'Setting up build configuration and signing'
  },
  {
    id: 'log_5',
    timestamp: '2024-12-08T14:28:00Z',
    level: 'info',
    message: 'Building iOS application',
    details: 'Running xcodebuild archive command'
  },
  {
    id: 'log_6',
    timestamp: '2024-12-08T14:32:00Z',
    level: 'info',
    message: 'Build completed successfully',
    details: 'Generated MyRestaurantApp.ipa ready for download'
  }
]

// Mock Apps
export const mockApps: AppConfig[] = [
  {
    id: 'app_1',
    name: 'My Restaurant App',
    description: 'Beautiful restaurant ordering app with delivery tracking',
    packageName: 'com.mycompany.restaurant',
    version: '1.0.0',
    buildNumber: 1,
    startUrl: 'https://myrestaurant.com',
    domain: 'myrestaurant.com',
    branding: {
      primaryColor: '#e74c3c',
      primaryColorDark: '#c0392b',
      accentColor: '#f39c12',
      backgroundColor: '#ffffff',
      companyName: 'My Restaurant LLC'
    },
    assets: {
      appIcon: mockAssets[0],
      splashScreen: mockAssets[1],
      additionalAssets: []
    },
    platforms: ['ios', 'android'],
    features: {
      locationServices: true,
      pushNotifications: true,
      cameraAccess: false,
      offlineMode: true,
      analytics: true
    },
    buildConfig: {
      environment: 'production',
      minSdkVersion: 24,
      targetSdkVersion: 34
    },
    createdAt: '2024-12-08T10:00:00Z',
    updatedAt: '2024-12-08T14:32:00Z',
    status: 'completed',
    builds: [
      {
        id: 'build_1',
        appId: 'app_1',
        platform: 'ios',
        status: 'success',
        progress: 100,
        startedAt: '2024-12-08T14:25:00Z',
        completedAt: '2024-12-08T14:32:00Z',
        downloadUrl: '/api/builds/build_1/download',
        buildLogs: mockBuildLogs
      },
      {
        id: 'build_2',
        appId: 'app_1',
        platform: 'android',
        status: 'building',
        progress: 65,
        startedAt: '2024-12-08T14:35:00Z',
        buildLogs: mockBuildLogs.slice(0, 4)
      }
    ]
  },
  {
    id: 'app_2',
    name: 'Fitness Tracker Pro',
    description: 'Complete fitness tracking with workout plans and nutrition',
    packageName: 'com.fitnesscorp.tracker',
    version: '2.1.0',
    buildNumber: 3,
    startUrl: 'https://fitnesstracker.app',
    domain: 'fitnesstracker.app',
    branding: {
      primaryColor: '#3498db',
      primaryColorDark: '#2980b9',
      accentColor: '#1abc9c',
      backgroundColor: '#ecf0f1',
      companyName: 'FitnessCorp Inc'
    },
    assets: {
      additionalAssets: []
    },
    platforms: ['ios', 'android'],
    features: {
      locationServices: true,
      pushNotifications: true,
      cameraAccess: true,
      offlineMode: false,
      analytics: true
    },
    buildConfig: {
      environment: 'development',
      minSdkVersion: 26,
      targetSdkVersion: 34
    },
    createdAt: '2024-11-15T09:20:00Z',
    updatedAt: '2024-11-20T16:45:00Z',
    status: 'configured',
    builds: []
  },
  {
    id: 'app_3',
    name: 'E-Commerce Store',
    description: 'Modern online shopping experience with wishlist and reviews',
    packageName: 'com.shopify.mystore',
    version: '1.2.0',
    buildNumber: 5,
    startUrl: 'https://mystore.shopify.com',
    domain: 'mystore.shopify.com',
    branding: {
      primaryColor: '#9b59b6',
      primaryColorDark: '#8e44ad',
      accentColor: '#e67e22',
      backgroundColor: '#ffffff',
      companyName: 'My E-Commerce Store'
    },
    assets: {
      additionalAssets: []
    },
    platforms: ['android'],
    features: {
      locationServices: false,
      pushNotifications: true,
      cameraAccess: true,
      offlineMode: true,
      analytics: true
    },
    buildConfig: {
      environment: 'staging',
      minSdkVersion: 24,
      targetSdkVersion: 33
    },
    createdAt: '2024-10-02T14:10:00Z',
    updatedAt: '2024-10-05T11:20:00Z',
    status: 'failed',
    builds: [
      {
        id: 'build_3',
        appId: 'app_3',
        platform: 'android',
        status: 'failed',
        progress: 45,
        startedAt: '2024-10-05T11:00:00Z',
        completedAt: '2024-10-05T11:20:00Z',
        errorMessage: 'Build failed during Gradle compilation. Invalid package name format.',
        buildLogs: [
          {
            id: 'log_error_1',
            timestamp: '2024-10-05T11:15:00Z',
            level: 'error',
            message: 'Gradle build failed',
            details: 'Invalid application ID format in build.gradle'
          }
        ]
      }
    ]
  }
]

// Mock Templates
export const mockTemplates: Template[] = [
  {
    id: 'template_ios',
    name: 'iOS Swift WebView',
    platform: 'ios',
    version: '1.0.0',
    description: 'Modern iOS app template with WebView, location services, and push notifications',
    features: [
      'WKWebView Integration',
      'JavaScript Bridge',
      'Location Services',
      'Push Notifications',
      'App Store Ready'
    ],
    requirements: {
      minSdkVersion: 14,
      targetSdkVersion: 17,
      swiftVersion: '5.0+'
    },
    placeholders: [
      {
        key: 'APP_NAME',
        description: 'Display name of the application',
        type: 'string',
        required: true,
        example: 'My Restaurant App',
        category: 'App Identity'
      },
      {
        key: 'BUNDLE_ID',
        description: 'iOS bundle identifier',
        type: 'string',
        required: true,
        pattern: '^[a-zA-Z0-9.-]+$',
        example: 'com.mycompany.myapp',
        category: 'App Identity'
      },
      {
        key: 'START_URL',
        description: 'Initial URL to load in the webview',
        type: 'url',
        required: true,
        example: 'https://myrestaurant.com',
        category: 'Web Configuration'
      },
      {
        key: 'PRIMARY_COLOR_HEX',
        description: 'Primary brand color in hex format',
        type: 'color',
        required: true,
        pattern: '^#[0-9A-Fa-f]{6}$',
        example: '#2196F3',
        category: 'Branding'
      }
    ],
    assetRequirements: [
      {
        type: 'icon',
        sizes: ['20x20', '29x29', '40x40', '58x58', '60x60', '80x80', '87x87', '120x120', '180x180', '1024x1024'],
        format: ['PNG'],
        description: 'App icons for all device sizes and App Store'
      },
      {
        type: 'splash',
        sizes: ['1125x2436', '1242x2688', '828x1792'],
        format: ['PNG'],
        description: 'Launch screen images for different device sizes'
      }
    ]
  },
  {
    id: 'template_android',
    name: 'Android Kotlin WebView',
    platform: 'android',
    version: '1.0.0',
    description: 'Modern Android app template with WebView, Material Design, and native features',
    features: [
      'Modern WebView',
      'Material Design UI',
      'JavaScript Bridge', 
      'Location Services',
      'Push Notifications',
      'Play Store Ready'
    ],
    requirements: {
      minSdkVersion: 24,
      targetSdkVersion: 34,
      kotlinVersion: '1.9.0+'
    },
    placeholders: [
      {
        key: 'APP_NAME',
        description: 'Display name of the application',
        type: 'string',
        required: true,
        example: 'My Restaurant App',
        category: 'App Identity'
      },
      {
        key: 'PACKAGE_NAME',
        description: 'Android package name',
        type: 'string',
        required: true,
        pattern: '^[a-z][a-z0-9_]*(\\.[a-z][a-z0-9_]*)*$',
        example: 'com.mycompany.myapp',
        category: 'App Identity'
      }
    ],
    assetRequirements: [
      {
        type: 'icon',
        sizes: ['48x48', '72x72', '96x96', '144x144', '192x192'],
        format: ['PNG'],
        description: 'App icons for different screen densities'
      }
    ]
  }
]

// Mock Dashboard Stats
export const mockDashboardStats: DashboardStats = {
  totalApps: 3,
  activeBuilds: 1,
  completedBuilds: 1,
  failedBuilds: 1,
  storageUsed: 1073741824, // 1GB
  storageLimit: 5368709120, // 5GB
  
  recentBuilds: mockApps.flatMap(app => app.builds).slice(0, 5),
  recentApps: mockApps.slice(0, 3),
  
  buildsOverTime: [
    { date: '2024-12-01', successful: 2, failed: 0 },
    { date: '2024-12-02', successful: 1, failed: 1 },
    { date: '2024-12-03', successful: 3, failed: 0 },
    { date: '2024-12-04', successful: 1, failed: 0 },
    { date: '2024-12-05', successful: 2, failed: 1 },
    { date: '2024-12-06', successful: 4, failed: 0 },
    { date: '2024-12-07', successful: 1, failed: 0 },
    { date: '2024-12-08', successful: 1, failed: 0 }
  ],
  
  platformDistribution: [
    { platform: 'ios', count: 2, percentage: 50 },
    { platform: 'android', count: 2, percentage: 50 }
  ]
}

// Utility functions for mock data
export const getMockAppById = (id: string): AppConfig | undefined => {
  return mockApps.find(app => app.id === id)
}

export const getMockBuildById = (id: string): Build | undefined => {
  for (const app of mockApps) {
    const build = app.builds.find(build => build.id === id)
    if (build) return build
  }
  return undefined
}

export const getMockTemplateById = (id: string): Template | undefined => {
  return mockTemplates.find(template => template.id === id)
}

// Simulate API delays for realistic development experience
export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// Mock API functions (to be replaced with real API calls)
export const mockApiCalls = {
  async getDashboardStats(): Promise<DashboardStats> {
    await delay(500)
    return mockDashboardStats
  },
  
  async getApps(): Promise<AppConfig[]> {
    await delay(300)
    return mockApps
  },
  
  async getAppById(id: string): Promise<AppConfig | null> {
    await delay(200)
    return getMockAppById(id) || null
  },
  
  async getTemplates(): Promise<Template[]> {
    await delay(400)
    return mockTemplates
  },
  
  async createApp(appData: Partial<AppConfig>): Promise<AppConfig> {
    await delay(1000)
    const newApp: AppConfig = {
      id: `app_${Date.now()}`,
      name: appData.name || 'New App',
      description: appData.description || '',
      packageName: appData.packageName || 'com.example.app',
      version: '1.0.0',
      buildNumber: 1,
      startUrl: appData.startUrl || 'https://example.com',
      domain: appData.domain || 'example.com',
      branding: appData.branding || {
        primaryColor: '#3b82f6',
        primaryColorDark: '#2563eb',
        accentColor: '#f59e0b',
        backgroundColor: '#ffffff',
        companyName: 'My Company'
      },
      assets: { additionalAssets: [] },
      platforms: appData.platforms || ['ios'],
      features: appData.features || {
        locationServices: false,
        pushNotifications: false,
        cameraAccess: false,
        offlineMode: false,
        analytics: false
      },
      buildConfig: {
        environment: 'development'
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'draft',
      builds: []
    }
    mockApps.unshift(newApp)
    return newApp
  },
  
  async startBuild(appId: string, platform: 'ios' | 'android'): Promise<Build> {
    await delay(500)
    const app = getMockAppById(appId)
    if (!app) throw new Error('App not found')
    
    const newBuild: Build = {
      id: `build_${Date.now()}`,
      appId,
      platform,
      status: 'queued',
      progress: 0,
      startedAt: new Date().toISOString(),
      buildLogs: [
        {
          id: `log_${Date.now()}`,
          timestamp: new Date().toISOString(),
          level: 'info',
          message: 'Build queued successfully',
          details: `Starting ${platform} build for ${app.name}`
        }
      ]
    }
    
    app.builds.unshift(newBuild)
    return newBuild
  }
} 